import { DOCUMENT } from '@angular/common';
import { inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { filter, first, map } from 'rxjs';

import { isAuthenticatedSessionState, SessionState } from '../services/session.models';
import { SessionService } from '../services/session.service';
import { AuthUser } from './auth.models';

export function canAccessInternalNonProductionFeature(
    user: Pick<AuthUser, 'isInternal'> | null | undefined,
    hostname: string,
): boolean {
    return (user?.isInternal ?? false) && hostname.toLowerCase() !== 'wolf.advania.no';
}

export const authGuard: CanActivateFn = (_route, state) => {
    const session = inject(SessionService);
    const router = inject(Router);

    return resolveSessionState(session, (sessionState) => {
        if (isAuthenticatedSessionState(sessionState)) {
            return true;
        }

        return router.createUrlTree(['/login'], {
            queryParams: {
                redirectUri: state.url === '/' ? undefined : state.url,
                error: sessionState === SessionState.AuthenticationError ? 'AUTH_STARTUP_FAILED' : undefined,
            },
        });
    });
};

export const internalUserGuard: CanActivateFn = (_route, state) => {
    const session = inject(SessionService);
    const router = inject(Router);

    return resolveSessionState(session, (sessionState) => {
        if (!isAuthenticatedSessionState(sessionState)) {
            return router.createUrlTree(['/login'], { queryParams: { redirectUri: state.url } });
        }

        return session.user()?.isInternal ? true : router.createUrlTree(['/']);
    });
};

export const internalNonProductionGuard: CanActivateFn = (_route, state) => {
    const session = inject(SessionService);
    const router = inject(Router);
    const hostname = inject(DOCUMENT).location.hostname;

    return resolveSessionState(session, (sessionState) => {
        if (!isAuthenticatedSessionState(sessionState)) {
            return router.createUrlTree(['/login'], { queryParams: { redirectUri: state.url } });
        }

        return canAccessInternalNonProductionFeature(session.user(), hostname) ? true : router.createUrlTree(['/']);
    });
};

function resolveSessionState(session: SessionService, resolve: (state: SessionState) => true | UrlTree) {
    const state = session.state();

    return state === SessionState.LoadingAuthentication
        ? toObservable(session.state).pipe(
              filter((nextState) => nextState !== SessionState.LoadingAuthentication),
              first(),
              map(resolve),
          )
        : resolve(state);
}
